<template>
  <layout-public>
    <page-meta />
    <!-- Hero -->
    <page-hero
      :crumbs="reportCrumbs"
      :title="report.business_name"
      :link="report.website_url"
      :meta="t('Prepared {date}', { date: formatDate(report.created_at) })"
    />

    <!-- Score gauges -->
    <section v-if="hasScores" class="report-scores">
      <div class="container">
        <div class="row g-3">
          <div v-for="score in scoreCards" :key="score.label" class="col-6 col-md-4 col-lg-2" data-aos="fade-up">
            <div class="score-card">
              <div class="score-ring" :class="scoreClass(score.value, score.max)">
                <span class="score-value">{{ score.display }}</span>
              </div>
              <p class="score-label">{{ score.label }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Executive summary -->
    <section v-if="report.data.executiveSummary" class="report-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-10" data-aos="fade-up">
            <section-title align="left" :title="t('Summary')" />
            <p class="lead">{{ report.data.executiveSummary }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Screenshot -->
    <section v-if="report.screenshot_image" class="report-section report-screenshot">
      <div class="container">
        <div class="row">
          <div class="col-lg-10">
            <section-title align="left" :title="t('Your website today')" data-aos="fade-up" />
            <div class="screenshot-frame" data-aos="zoom-in">
              <img :src="report.screenshot_image" :alt="t('Screenshot of {site}', { site: report.business_name })" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SEO visibility -->
    <section v-if="hasSeoStats" class="report-section report-seo">
      <div class="container">
        <div class="row">
          <div class="col-lg-10" data-aos="fade-up">
            <section-title align="left" :title="t('How visible are you on Google?')" />

            <div v-if="seoStats.reviews" class="seo-meta mb-4">
              <span v-if="seoStats.reviews.rating">
                {{ t('Google rating:') }} <strong>{{ seoStats.reviews.rating }}</strong>
                <span v-if="seoStats.reviews.count"> {{ t('({count} reviews)', { count: seoStats.reviews.count }) }}</span>
              </span>
              <span v-if="seoStats.indexedPages !== undefined" class="ms-3">
                {{ t('Indexed pages:') }} <strong>{{ indexedPagesLabel }}</strong>
              </span>
            </div>

            <div v-if="seoStats.keywordRankings && seoStats.keywordRankings.length" class="table-scroll">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>{{ t('Search phrase') }}</th>
                    <th>{{ t('Your position') }}</th>
                    <th>{{ t('Who ranks above you') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in seoStats.keywordRankings" :key="i">
                    <td>{{ row.keyword }}</td>
                    <td>
                      <!-- Ranked and unranked are two inks, not two Bootstrap
                           status colours: the old pair was amber on white for
                           "ranked" and red for "not ranked", which read as a
                           warning the data does not support and put an amber
                           #ffc107 on a white cell at about 2:1 contrast. -->
                      <span :class="row.position ? 'rank-found' : 'rank-missing'">
                        {{ row.position ? '#' + row.position : t('Not in top 20') }}
                      </span>
                    </td>
                    <td>
                      <span v-if="row.competitors && row.competitors.length">
                        {{ row.competitors.map(c => c.name).join(', ') }}
                      </span>
                      <span v-else class="cell-empty">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Findings -->
    <section v-if="report.data.findings && report.data.findings.length" class="report-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-10">
            <section-title align="left" :title="t('Key findings')" data-aos="fade-up" />
            <div class="row g-3">
              <div v-for="(finding, i) in report.data.findings" :key="i" class="col-md-6" data-aos="fade-up">
                <div class="finding-card">
                  <span class="severity-badge" :class="'severity-' + finding.severity">{{ finding.severity }}</span>
                  <span class="category-tag">{{ finding.category }}</span>
                  <h3>{{ finding.title }}</h3>
                  <p class="finding-evidence"><strong>{{ t('Evidence:') }}</strong> {{ finding.evidence }}</p>
                  <p class="finding-impact">{{ finding.businessImpact }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- What works -->
    <section v-if="report.data.whatWorks && report.data.whatWorks.length" class="report-section report-positive">
      <div class="container">
        <div class="row">
          <div class="col-lg-10" data-aos="fade-up">
            <section-title align="left" :title="t('What\'s already working')" />
            <ul class="works-list">
              <li v-for="(item, i) in report.data.whatWorks" :key="i">{{ item }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Recommendations -->
    <section v-if="report.data.recommendations && report.data.recommendations.length" class="report-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-10">
            <section-title align="left" :title="t('Our recommendations')" data-aos="fade-up" />
            <div class="row g-3">
              <div v-for="(rec, i) in report.data.recommendations" :key="i" class="col-md-6" data-aos="fade-up">
                <div class="rec-card">
                  <h3>{{ rec.title }}</h3>
                  <p>{{ rec.detail }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mockup -->
    <section v-if="report.mockup_image" class="report-section report-mockup">
      <div class="container">
        <div class="row">
          <div class="col-lg-10">
            <section-title
              align="left"
              data-aos="fade-up"
              :title="t('What {site} could look like', { site: report.business_name })"
              :lede="t('A redesign concept prepared in your brand colours by our team, so you can visualise exactly what the website could look like before you commit.')"
            />
            <div class="mockup-frame" data-aos="zoom-in">
              <img :src="report.mockup_image" :alt="t('Redesign mockup for {site}', { site: report.business_name })" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="report-cta dark">
      <div class="container">
        <div class="row">
          <div class="col-lg-8" data-aos="fade-up">
            <section-title align="left" tone="brand" :title="t('Ready to improve your online presence?')" />
            <p v-if="report.data.suggestedServices && report.data.suggestedServices.length" class="mb-3">
              {{ t('Based on this review, we recommend:') }}
            </p>
            <ul v-if="report.data.suggestedServices && report.data.suggestedServices.length" class="services-list">
              <li v-for="(svc, i) in report.data.suggestedServices" :key="i">
                <strong>{{ serviceLabel(svc.service) }}</strong> - {{ svc.justification }}
              </li>
            </ul>
            <p class="promo-note mt-3">{{ t('We currently have a promotion on web design, SEO, and sponsored ads packages.') }}</p>
            <div class="action-row">
              <app-button :href="contactEmailHref" tone="dark" icon="envelope">
                {{ t('Email us') }}
              </app-button>
              <app-button
                :href="whatsappHref"
                target="_blank"
                rel="noopener"
                tone="dark"
                variant="ghost"
                :icon="['fab', 'whatsapp']"
              >
                {{ t('WhatsApp') }}
              </app-button>
            </div>
            <p class="report-footer-note mt-4">
              {{ t('This review was prepared by {site}, a Malaysian web design and digital marketing studio.', { site: publisher }) }}
            </p>
          </div>
        </div>
      </div>
    </section>
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.report-scores {
  background: $ground-tint;
}

.score-card {
  text-align: center;

  .score-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.5rem;
    font-weight: 700;
    font-size: 1.1rem;
    border: 4px solid $line;

    &.score-good {
      border-color: $status-good-ink;
      color: $status-good-ink;
    }

    &.score-ok {
      border-color: $status-warn-ink;
      color: $status-warn-ink;
    }

    &.score-bad {
      border-color: $status-bad-ink;
      color: $status-bad-ink;
    }
  }

  .score-label {
    font-size: 0.75rem;
    color: $text-label;
    margin: 0;
    line-height: 1.2;
  }
}

/* The report's framed media, shared by the screenshot and the redesign mockup. */
.screenshot-frame, .mockup-frame {
  border: 1px solid $line;
  border-radius: $r-xs;
  overflow: hidden;
  max-height: 600px;
  overflow-y: auto;
  box-shadow: $shadow-card;

  img {
    width: 100%;
    display: block;
  }

  /* A full-page screenshot scaled to a phone width is taller than the screen, so
     a fixed 600px frame would be an inner scroll region taller than the viewport
     it sits in. */
  @media (max-width: 767.98px) {
    max-height: 65vh;
  }
}

.finding-card, .rec-card {
  background: $ground;
  border: 1px solid $line;
  border-radius: $r-xs;
  padding: 1.25rem;
  height: 100%;

  h3 {
    font-size: 1.1rem;
    margin: 0.5rem 0;
  }
}

.severity-badge {
  display: inline-block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.15rem 0.5rem;
  border-radius: $r-xs;
  font-weight: 600;

  /* High, medium and low read as the status palette; "low" is the quiet one
     rather than a fourth colour, so it takes the brand blue's tint. */
  &.severity-high {
    background: $status-bad-ground;
    color: $status-bad-ink;
  }

  &.severity-medium {
    background: $status-warn-ground;
    color: $status-warn-ink;
  }

  &.severity-low {
    background: $tint-primary;
    color: $brand-deep;
  }
}

/* The rank cell and the empty cell of the search-phrase table. Both are inks
   from the token set rather than Bootstrap's status utilities, which on this
   page put amber type on a white cell. */
.rank-found {
  color: $text-strong;
  font-weight: 600;
}

.rank-missing,
.cell-empty {
  color: $text-muted;
}

.category-tag {
  display: inline-block;
  font-size: 0.7rem;
  background: $line;
  padding: 0.15rem 0.5rem;
  border-radius: $r-xs;
  margin-left: 0.25rem;
  text-transform: capitalize;
}

.finding-evidence {
  font-size: 0.9rem;
  color: $text-strong;
}

.finding-impact {
  font-size: 0.9rem;
  color: $text-label;
  margin-bottom: 0;
}

.report-positive {
  background: $ground-tint;
}

.works-list {
  padding-left: 1.25rem;
  li { margin-bottom: 0.5rem; }
}

.report-mockup {
  background: $ground-tint;
}

.report-cta {
  background: $grad-deep;
  color: $on-dark-strong;

  .services-list {
    list-style: none;
    padding: 0;
    li {
      padding: 0.5rem 0;
      border-bottom: 1px solid $on-dark-line;
    }
  }

  .promo-note {
    color: $on-dark-body;
  }

  .report-footer-note {
    color: $on-dark-muted;
    font-size: 0.85rem;
  }
}
</style>

<script>
import { useI18n } from 'vue-i18n';
import PageMeta from '@/Components/PageMeta.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import AppButton from '@/Components/AppButton.vue';
import PageHero from '@/Components/PageHero.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import { websiteReportMessages } from '@/i18n/websiteReport';
import { whatsappHref } from '@/whatsapp';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';

FontAwesomeLibrary.add(faEnvelope);

export default {
  components: { AppButton, PageMeta, LayoutPublic, PageHero, SectionTitle },

  props: {
    report: { type: Object, required: true },
  },

  computed: {
    /* The report is a single page rather than a branch of the site, so its one
       crumb is its own name: it marks the page without pretending to be a
       navigable path. */
    reportCrumbs() {
      return [{ label: this.t('Website Review') }];
    },

    scores() {
      return this.report.data.scores || {};
    },

    hasScores() {
      const s = this.scores;
      return (
        this.hasScore(s.performanceMobile) ||
        this.hasScore(s.performanceDesktop) ||
        this.hasScore(s.seo) ||
        this.hasScore(s.accessibility) ||
        this.hasScore(s.bestPractices) ||
        this.hasScore(this.report.data.aestheticsScore)
      );
    },

    scoreCards() {
      const s = this.scores;
      const cards = [];

      if (this.hasScore(s.performanceMobile)) cards.push({ label: this.t('Performance (Mobile)'), value: s.performanceMobile, max: 100, display: s.performanceMobile });
      if (this.hasScore(s.performanceDesktop)) cards.push({ label: this.t('Performance (Desktop)'), value: s.performanceDesktop, max: 100, display: s.performanceDesktop });
      if (this.hasScore(s.seo)) cards.push({ label: this.t('SEO'), value: s.seo, max: 100, display: s.seo });
      if (this.hasScore(s.accessibility)) cards.push({ label: this.t('Accessibility'), value: s.accessibility, max: 100, display: s.accessibility });
      if (this.hasScore(s.bestPractices)) cards.push({ label: this.t('Best Practices'), value: s.bestPractices, max: 100, display: s.bestPractices });
      if (this.hasScore(this.report.data.aestheticsScore)) cards.push({ label: this.t('Design'), value: this.report.data.aestheticsScore, max: 10, display: this.report.data.aestheticsScore + '/10' });

      return cards;
    },

    seoStats() {
      return this.report.data.seoStats || {};
    },

    hasSeoStats() {
      const s = this.seoStats;
      return (s.keywordRankings && s.keywordRankings.length) || s.indexedPages !== undefined || s.reviews;
    },

    indexedPagesLabel() {
      const count = this.seoStats.indexedPages;
      if (count === undefined || count === null) return '-';
      return count >= 10 ? '10+' : String(count);
    },

    /* The report is a client-facing artefact, so its one email and one phone
       come from the same shared business block the footer prints. The page
       previously carried `sales@` where the rest of the site says `support@`,
       and a placeholder WhatsApp number that belonged to no one. */
    business() {
      return this.$page.props.business;
    },

    publisher() {
      return this.business.name;
    },

    contactEmailHref() {
      return 'mailto:' + this.business.email;
    },

    whatsappHref() {
      return whatsappHref(this.business.phone, this.$page.props.locale, this.$page.props.referral);
    },
  },

  setup() {
    const { t } = useI18n({ messages: websiteReportMessages });

    return { t };
  },

  methods: {
    formatDate(iso) {
      return new Date(iso).toLocaleDateString('en-MY', { year: 'numeric', month: 'long', day: 'numeric' });
    },

    /* A zero is a score, not an absent one. Lighthouse returns 0 for a page that
       fails every audit, and reading a score for truth would drop that card and,
       if it were the only one, the whole gauge row. Only null and undefined mean
       the report did not measure it. */
    hasScore(value) {
      return value !== null && value !== undefined;
    },

    scoreClass(value, max) {
      const pct = max === 10 ? value * 10 : value;
      if (pct >= 90) return 'score-good';
      if (pct >= 50) return 'score-ok';
      return 'score-bad';
    },

    serviceLabel(service) {
      const labels = {
        website_redesign: this.t('Website Redesign'),
        advanced_seo: this.t('Advanced SEO'),
        sponsored_ads: this.t('Sponsored Ads'),
      };
      return labels[service] || service;
    },
  },
};
</script>
